import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import StaffAlerts from "@/components/StaffAlerts";
import {
  House, ChartBar, Receipt, ForkKnife, GridFour,
  Users, Package, IdentificationCard, Tag, ChartLine,
  CookingPot, SignOut
} from "@phosphor-icons/react";

const NAV = [
  { to: "/app", label: "Dashboard", icon: House, roles: ["admin", "manager"] },
  { to: "/app/pos", label: "POS Billing", icon: Receipt, roles: ["admin", "manager", "cashier", "waiter"] },
  { to: "/app/tables", label: "Tables", icon: GridFour, roles: ["admin", "manager", "cashier", "waiter"] },
  { to: "/app/orders", label: "Orders", icon: ForkKnife, roles: ["admin", "manager", "cashier", "waiter", "kitchen", "customer"] },
  { to: "/app/kds", label: "Kitchen (KDS)", icon: CookingPot, roles: ["admin", "manager", "kitchen"] },
  { to: "/app/menu", label: "Menu", icon: ChartBar, roles: ["admin", "manager"] },
  { to: "/app/inventory", label: "Inventory", icon: Package, roles: ["admin", "manager", "kitchen"] },
  { to: "/app/employees", label: "Employees", icon: IdentificationCard, roles: ["admin", "manager"] },
  { to: "/app/customers", label: "Customers", icon: Users, roles: ["admin", "manager", "cashier"] },
  { to: "/app/coupons", label: "Coupons", icon: Tag, roles: ["admin", "manager"] },
  { to: "/app/analytics", label: "Analytics", icon: ChartLine, roles: ["admin", "manager"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const visible = NAV.filter((n) => !n.roles || (user && n.roles.includes(user.role)));

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[var(--bg-primary)]">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-[#EAE7E0] bg-white" data-testid="app-sidebar">
        <div className="px-6 py-6 border-b border-[#EAE7E0]">
          <Link to="/app" className="flex items-center gap-3" data-testid="brand-link">
            <div className="w-10 h-10 rounded-lg bg-[var(--brand)] text-white grid place-items-center font-display font-black text-xl">R</div>
            <div>
              <div className="font-display font-extrabold text-lg leading-none">Resto</div>
              <div className="label-eyebrow text-[10px] mt-1">Manager</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {visible.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/app"}
              data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? "bg-[var(--brand)] text-white shadow-sm"
                    : "text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#EAE7E0]">
          <div className="flex items-center gap-3 p-2 rounded-md bg-[var(--bg-secondary)]">
            <div className="w-9 h-9 rounded-full bg-[var(--brand-2)] text-white grid place-items-center text-sm font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-[var(--text-secondary)]">{user?.role}</div>
            </div>
            <button
              onClick={doLogout}
              data-testid="logout-btn"
              className="p-2 rounded-md hover:bg-white transition-colors"
              title="Sign out"
            >
              <SignOut size={18} weight="bold" />
            </button>
          </div>
          <div className="mt-2 flex justify-end px-1">
            <StaffAlerts />
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden glass sticky top-0 z-30 px-4 py-3 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2" data-testid="mobile-brand">
            <div className="w-8 h-8 rounded-md bg-[var(--brand)] text-white grid place-items-center font-display font-black">R</div>
            <span className="font-display font-bold">Resto</span>
          </Link>
          <div className="flex items-center gap-2">
            <StaffAlerts />
            <button onClick={doLogout} data-testid="mobile-logout" className="text-xs font-semibold uppercase tracking-wider">
              Sign out
            </button>
          </div>
        </header>

        {/* Mobile nav scroll */}
        <nav className="lg:hidden flex gap-2 px-4 py-2 overflow-x-auto border-b border-[#EAE7E0] bg-white">
          {visible.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/app"}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap ${
                  isActive ? "bg-[var(--brand)] text-white" : "bg-[var(--bg-secondary)] text-[var(--text-primary)]"
                }`
              }
              data-testid={`mnav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <Icon size={14} weight="duotone" />
              {label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden" data-testid="app-main">
          {children}
        </main>
      </div>
    </div>
  );
}
